import { useContext } from "react";
import {
  CardView,
  Card,
  CardPreview,
  Text,
  Image,
  Heading,
  ActionButtonGroup,
  ActionButton,
  ProgressCircle,
  Skeleton,
  LinkButton,
} from "@react-spectrum/s2";
import RefreshIcon from "@react-spectrum/s2/icons/Refresh";
import BugIcon from "@react-spectrum/s2/icons/Bug";
import DownloadIcon from "@react-spectrum/s2/icons/Download";
import ExpressIcon from "icon:../../../assets/AdobeExpressIcon.svg";

import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { Step } from "./Step";
import { DevToolbarItem } from "../DevToolbar";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };
import { headingStyle } from "../style" with { type: "macro" };

export const BannersStep = ({ onContinue, onBack, continueLabel }) => {
  const {
    template,
    video,
    updateVideo,
    assetsTemplate,
    bannersStatus,
    generateBanners,
    exportBanners,
    downloadAllAssets,
    regenerateBannersFromCurrentAssets,
  } = useContext(VideoWizardContext);

  return (
    <>
      <Step
        styles={style({
          gridArea: "content",
          display: "flex",
          flexDirection: "column",
          size: "full",
          maxWidth: 1200,
          marginX: "auto",
        })}
      >
        <Step.Header
          title="Final campaign assets"
          subtitle="These banners combine your brand, message, and generated visuals."
        />

        <Heading styles={style(headingStyle({ marginBottom: 0 }))}>
          {!!bannersStatus && (
            <div
              className={style({
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginStart: 8,
              })}
            >
              <ProgressCircle
                isIndeterminate
                size="S"
                staticColor="auto"
                aria-label="Generating assets renditions"
              />
              <Text styles={style({ font: "detail" })}>{bannersStatus}</Text>
            </div>
          )}
          <ActionButtonGroup
            isQuiet
            staticColor="auto"
            styles={style({ marginStart: "auto" })}
          >
            <ActionButton
              isDisabled={generateBanners.isPending || exportBanners.isFetching}
              onPress={() => exportBanners.refetch()}
            >
              <RefreshIcon />
              <Text>Refresh</Text>
            </ActionButton>
            <ActionButton
              isPending={downloadAllAssets.isPending}
              isDisabled={!!bannersStatus}
              onPress={() => downloadAllAssets.mutate()}
            >
              <DownloadIcon />
              <Text>Download all</Text>
            </ActionButton>
            <LinkButton
              isDisabled={
                !video.data?.templateAssetsRenditions?.[0]?.documentId
              }
              href={`https://new.express.adobe.com/id/${video.data?.templateAssetsRenditions?.[0]?.documentId}`}
              target="_blank"
              variant="secondary"
            >
              <ExpressIcon />
              <Text>Edit in Express</Text>
            </LinkButton>
          </ActionButtonGroup>
        </Heading>

        <CardView
          layout="waterfall"
          variant="quiet"
          size="L"
          selectionMode="none"
          aria-label="Assets variations"
        >
          {assetsTemplate.data?.pages?.map((apiPage) =>
            apiPage.documentPages.map((page) => (
              <Card
                key={page.pageTitle + page.pageNumber}
                textValue={page.pageTitle || `Page ${page.pageNumber}`}
              >
                <CardPreview>
                  <Skeleton
                    isLoading={
                      generateBanners.isPending || exportBanners.isFetching
                    }
                  >
                    <Image
                      src={
                        exportBanners.data?.find(
                          (rendition) =>
                            rendition.pageNumber === page.pageNumber,
                        )?.renditionUrl
                      }
                      alt={page.pageTitle || `Page ${page.pageNumber}`}
                      styles={style({ objectFit: "scale-down" })}
                      UNSAFE_style={{
                        aspectRatio: `${page.size.width} / ${page.size.height}`,
                      }}
                    />
                  </Skeleton>
                </CardPreview>
              </Card>
            )),
          )}
        </CardView>
      </Step>

      <Step.Actions
        onBack={onBack}
        onContinue={onContinue}
        continueVariant="genai"
        continueLabel={continueLabel}
        isContinueDisabled={
          !!template.data?.assetsTemplateId
            ? !exportBanners.isSuccess ||
              exportBanners.isFetching ||
              generateBanners.isPending
            : false
        }
      />

      <DevToolbarItem
        isDisabled={
          (!video.data?.videoPath && !video.data?.expressId) ||
          updateVideo.isPending
        }
        onPress={() => {
          updateVideo.mutate({
            status: null,
            expressId: null,
            videoPath: null,
          });
        }}
      >
        Clear video data
      </DevToolbarItem>
      <DevToolbarItem
        isDisabled={
          updateVideo.isPending ||
          generateBanners.isPending ||
          exportBanners.isFetching
        }
        onPress={regenerateBannersFromCurrentAssets}
      >
        Regenerate
      </DevToolbarItem>
    </>
  );
};
